import React, { useEffect, useMemo, useRef, useState } from "react";

import {
  MAX_RESTOCK_ROW_QUANTITY,
  fill,
  getLocationStock,
  getVariantImage,
  getVariantLabel,
  getVariantStock,
} from "../studioEditModel";
import { Btn, Drawer, EmptyState, Field, Icon, Notice, Select, Stepper, Thumb, Toggle, cx } from "../StudioUi";

const normalize = (value) =>
  String(value || "")
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

const toQuantity = (value) => {
  const parsed = Number.parseInt(String(value || "").replace(/[^0-9]/g, ""), 10);
  return Number.isFinite(parsed) ? Math.min(parsed, MAX_RESTOCK_ROW_QUANTITY) : 0;
};

/**
 * Builds the restock payload: only checked rows with a positive whole
 * quantity are sent. The API adds these units to "on hand" at one location;
 * it never replaces the current quantity and never touches reservations.
 */
export const buildRestockPayload = (locationId, selectedIds, quantities, needsLabels = true) => ({
  locationId: Number(locationId),
  items: Array.from(selectedIds)
    .map((variantId) => ({ variantId: Number(variantId), quantity: toQuantity(quantities[variantId]) }))
    .filter((item) => item.quantity > 0),
  // Whether the received items still need Black & White labels (Print Center).
  needsLabels: Boolean(needsLabels),
});

/**
 * One idempotency key per receiving action: a double click or a retried request
 * with the same rows reuses it (the server then records the stock once), while
 * any change to the rows starts a new action with a new key.
 */
export const nextRestockIdempotencyKey = (previous, payload) => {
  const fingerprint = JSON.stringify(payload);
  if (previous && previous.fingerprint === fingerprint) return previous;
  const random =
    typeof window !== "undefined" && window.crypto?.randomUUID
      ? window.crypto.randomUUID()
      : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
  return { fingerprint, key: `restock-${random}` };
};

const VISIBLE_UNIT_BARCODES = 2;

/**
 * The new units' own barcodes (what gets printed and sold), compactly: the
 * first two and "+N more". The variant barcode is never shown for
 * serialized stock because it is not a sellable label.
 */
export const UnitBarcodes = ({ tt, barcodes }) => {
  if (!barcodes.length) return <span className="pse-muted pse-small">{tt("rsUnitBarcodesPending")}</span>;
  const shown = barcodes.slice(0, VISIBLE_UNIT_BARCODES);
  const hidden = barcodes.length - shown.length;
  return (
    <span className="pse-muted pse-small pse-unit-barcodes" title={barcodes.join(", ")}>
      {shown.join(", ")}
      {hidden > 0 ? ` ${fill(tt("rsMoreUnits"), { count: hidden })}` : ""}
    </span>
  );
};

const RestockDrawer = ({
  tt,
  isOpen,
  defaultVariantId,
  onClose,
  variants,
  media,
  colorOption,
  locations,
  inventoryMode,
  onSubmit,
  onPrint,
  onOpenPrintCenter,
  isSaving,
}) => {
  const isSerialized = inventoryMode === "serialized";
  const [locationId, setLocationId] = useState("");
  const [search, setSearch] = useState("");
  const [quantities, setQuantities] = useState({});
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkQuantity, setBulkQuantity] = useState("");
  const [result, setResult] = useState(null);
  const [needsLabels, setNeedsLabels] = useState(true);

  useEffect(() => {
    if (!isOpen) return;
    const preset = (variants || []).find((variant) => variant.id === defaultVariantId);
    setLocationId(locations?.[0]?.id ? String(locations[0].id) : "");
    setSearch(preset ? getVariantLabel(preset) || preset.sku || "" : "");
    setQuantities({});
    setSelectedIds(new Set(preset ? [preset.id] : []));
    setBulkQuantity("");
    setResult(null);
    setNeedsLabels(true);
    // Reset only when the drawer opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const location = (locations || []).find((entry) => String(entry.id) === String(locationId));
  const locationLabel = location?.name || fill(tt("locationFallback"), { id: locationId || "—" });

  const allRows = useMemo(
    () =>
      (variants || []).map((variant) => {
        const current = isSerialized
          ? getVariantStock(variant, inventoryMode).available
          : getLocationStock(variant, locationId).onHand;
        const levelSource = isSerialized ? getVariantStock(variant, inventoryMode) : getLocationStock(variant, locationId);
        return {
          variant,
          label: getVariantLabel(variant) || fill(tt("variantFallback"), { id: variant.id }),
          image: getVariantImage(variant, media, colorOption),
          current,
          level: levelSource.level,
          haystack: normalize([getVariantLabel(variant), variant.title, variant.sku, variant.barcode].join(" ")),
        };
      }),
    [colorOption, inventoryMode, isSerialized, locationId, media, tt, variants]
  );

  const rows = useMemo(() => {
    const query = normalize(search);
    return query ? allRows.filter((row) => row.haystack.includes(query)) : allRows;
  }, [allRows, search]);

  const lowRows = allRows.filter((row) => row.level !== "ok");
  // Serialized units get new barcodes, so they always need labels.
  const payload = buildRestockPayload(locationId, selectedIds, quantities, isSerialized || needsLabels);
  const totalUnits = payload.items.reduce((sum, item) => sum + item.quantity, 0);

  const setQuantity = (variantId, value) => {
    setQuantities((current) => ({ ...current, [variantId]: value }));
    setSelectedIds((current) => {
      const next = new Set(current);
      if (toQuantity(value) > 0) next.add(variantId);
      return next;
    });
  };
  const toggleRow = (variantId) => {
    const isChecked = selectedIds.has(variantId);
    setSelectedIds((current) => {
      const next = new Set(current);
      if (isChecked) next.delete(variantId);
      else next.add(variantId);
      return next;
    });
    // Unticking a row also clears its quantity, so nothing hidden is saved.
    if (isChecked) setQuantities((current) => ({ ...current, [variantId]: "" }));
  };
  const selectLow = () =>
    setSelectedIds((current) => new Set([...current, ...lowRows.map((row) => row.variant.id)]));
  const applyBulk = () => {
    const quantity = toQuantity(bulkQuantity);
    if (!quantity) return;
    setQuantities((current) => {
      const next = { ...current };
      selectedIds.forEach((id) => {
        next[id] = String(quantity);
      });
      return next;
    });
  };
  const clearAll = () => {
    setQuantities({});
    setSelectedIds(new Set());
    setBulkQuantity("");
  };

  const idempotencyRef = useRef(null);
  const handleSubmit = async () => {
    if (!payload.locationId || !payload.items.length) return;
    idempotencyRef.current = nextRestockIdempotencyKey(idempotencyRef.current, payload);
    const response = await onSubmit({ ...payload, idempotencyKey: idempotencyRef.current.key });
    if (response) {
      idempotencyRef.current = null;
      setResult(response);
    }
  };

  const canSubmit = Boolean(locationId) && payload.items.length > 0 && !isSaving;

  if (result) {
    const labelsToPrint = Number(result.receipt?.labelsOutstanding || 0);
    // Marked as already labelled: offer no print action that suggests otherwise.
    const markedLabelled = Boolean(result.receipt) && !result.receipt.needsLabels;
    return (
      <Drawer
        isOpen={isOpen}
        onClose={onClose}
        title={tt("rsSuccessTitle")}
        description={markedLabelled ? tt("rsSuccessCopyLabelled") : tt("rsSuccessCopy")}
        icon="checkCircle"
        size="lg"
        closeLabel={tt("close")}
        footer={
          <>
            <Btn variant="ghost" onClick={onClose}>
              {tt("done")}
            </Btn>
            {labelsToPrint > 0 && onOpenPrintCenter ? (
              <Btn variant="primary" icon="printer" onClick={() => onOpenPrintCenter(result.receipt)}>
                {fill(tt(labelsToPrint === 1 ? "rsPrintLabelsOne" : "rsPrintLabels"), { count: labelsToPrint })}
              </Btn>
            ) : markedLabelled ? null : (
              <Btn variant="primary" icon="printer" disabled={!result.printableItems?.length} onClick={() => onPrint(result)}>
                {tt("rsPrintNow")}
              </Btn>
            )}
          </>
        }
      >
        <div className="pse-summary-tiles">
          <div className="pse-summary-tile">
            <span>{tt("rsSuccessVariants")}</span>
            <strong>{result.variantCount}</strong>
          </div>
          <div className="pse-summary-tile pse-summary-tile--accent">
            <span>{tt("rsSuccessUnits")}</span>
            <strong>+{result.totalUnits}</strong>
          </div>
        </div>
        <ul className="pse-result-list">
          {result.items.map((item) => (
            <li key={item.variantId}>
              <span>{item.variant ? getVariantLabel(item.variant) : fill(tt("variantFallback"), { id: item.variantId })}</span>
              {Array.isArray(item.unitBarcodes) ? (
                <UnitBarcodes tt={tt} barcodes={item.unitBarcodes} />
              ) : (
                <span className="pse-muted pse-small">{item.variant?.barcode || "—"}</span>
              )}
              <strong className="pse-positive">+{item.quantity}</strong>
            </li>
          ))}
        </ul>
        {labelsToPrint > 0 ? (
          <Notice tone="info" icon="printer">
            {fill(tt(labelsToPrint === 1 ? "rsLabelsNeededOne" : "rsLabelsNeeded"), { count: labelsToPrint })}
          </Notice>
        ) : result.receipt && !result.receipt.needsLabels ? (
          <Notice tone="info">{tt("rsLabelsNotNeeded")}</Notice>
        ) : null}
        {!labelsToPrint && !markedLabelled && !result.printableItems?.length ? (
          <Notice tone="info">{tt("rsNoPrintable")}</Notice>
        ) : null}
      </Drawer>
    );
  }

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      title={tt("rsTitle")}
      description={tt("rsCopy")}
      icon="cube"
      size="lg"
      closeLabel={tt("close")}
      footer={
        <div className="pse-drawer-foot-stack">
          <div className="pse-restock-labels">
            <Toggle
              checked={isSerialized || needsLabels}
              disabled={isSerialized}
              onChange={setNeedsLabels}
              label={tt("rsNeedsLabels")}
              description={
                isSerialized ? tt("rsNeedsLabelsSerialized") : needsLabels ? tt("rsNeedsLabelsOn") : tt("rsNeedsLabelsOff")
              }
            />
          </div>
          <Notice tone="warning" className="pse-notice--compact">
            {fill(isSerialized ? tt("rsFooterNoteSerialized") : tt("rsFooterNote"), { location: locationLabel })}
          </Notice>
          <div className="pse-drawer-foot-actions">
            <Btn variant="ghost" onClick={onClose} disabled={isSaving}>
              {tt("cancel")}
            </Btn>
            <Btn variant="primary" icon="check" onClick={handleSubmit} disabled={!canSubmit} className="pse-btn--grow">
              {isSaving
                ? tt("rsSaving")
                : `${tt("rsSave")} (${fill(tt(payload.items.length === 1 ? "rsSaveSummaryOne" : "rsSaveSummary"), {
                    variants: payload.items.length,
                    units: totalUnits,
                  })}${payload.needsLabels ? "" : ` · ${tt("rsSaveNoLabels")}`})`}
            </Btn>
          </div>
        </div>
      }
    >
      <div className="pse-summary-tiles">
        <div className="pse-summary-tile">
          <Icon name="cart" size={22} />
          <span>{tt("rsSelectedVariants")}</span>
          <strong>{payload.items.length}</strong>
          <small>{fill(tt("rsOfTotal"), { count: allRows.length })}</small>
        </div>
        <div className="pse-summary-tile pse-summary-tile--accent">
          <Icon name="layers" size={22} />
          <span>{tt("rsUnitsToAdd")}</span>
          <strong>{totalUnits}</strong>
          <small>{tt("rsUnitsTotal")}</small>
        </div>
      </div>

      <div className="pse-grid pse-grid--restock">
        <Field label={tt("rsLocation")} htmlFor="pse-restock-location">
          <Select id="pse-restock-location" value={locationId} onChange={setLocationId}>
            <option value="">{tt("rsChooseLocation")}</option>
            {(locations || []).map((entry) => (
              <option key={entry.id} value={entry.id}>
                {entry.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={tt("rsSearch")} htmlFor="pse-restock-search">
          <label className="pse-search">
            <Icon name="search" size={17} />
            <input
              id="pse-restock-search"
              type="search"
              value={search}
              placeholder={tt("rsSearchPlaceholder")}
              onChange={(event) => setSearch(event.target.value)}
            />
          </label>
        </Field>
      </div>

      <div className="pse-restock-tools">
        <button
          type="button"
          className="pse-chip-button"
          onClick={selectLow}
          disabled={!lowRows.length}
          title={tt("rsSelectLowHint")}
        >
          <Icon name="alert" size={15} />
          {fill(tt("rsSelectLow"), { count: lowRows.length })}
        </button>
        {selectedIds.size ? (
          <div className="pse-restock-bulk">
            <Stepper
              value={bulkQuantity}
              onChange={setBulkQuantity}
              max={MAX_RESTOCK_ROW_QUANTITY}
              ariaLabel={tt("rsColAdd")}
              decreaseLabel={tt("rsDecrease")}
              increaseLabel={tt("rsIncrease")}
            />
            <Btn variant="secondary" size="sm" onClick={applyBulk} disabled={!toQuantity(bulkQuantity)}>
              {`${tt("rsColAdd")} → ${selectedIds.size}`}
            </Btn>
          </div>
        ) : null}
        <button type="button" className="pse-link" onClick={clearAll}>
          {tt("rsClear")}
        </button>
      </div>

      {rows.length ? (
        <div className="pse-restock-list" role="list">
          <div className="pse-restock-row pse-restock-row--head" aria-hidden="true">
            <span />
            <span>{tt("rsColVariant")}</span>
            <span className="pse-table__num">{isSerialized ? tt("rsColCurrentSerialized") : tt("rsColCurrent")}</span>
            <span className="pse-restock-row__center">{tt("rsColAdd")}</span>
            <span className="pse-table__num">{tt("rsColResult")}</span>
          </div>
          {rows.map((row) => {
            const quantity = toQuantity(quantities[row.variant.id]);
            const checked = selectedIds.has(row.variant.id);
            return (
              <div
                key={row.variant.id}
                role="listitem"
                className={cx("pse-restock-row", checked && "is-checked", quantity > 0 && "has-quantity")}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  aria-label={row.label}
                  onChange={() => toggleRow(row.variant.id)}
                />
                <span className="pse-restock-row__variant">
                  <Thumb src={row.image?.sourceUrl} size={40} alt="" />
                  <span className="pse-restock-row__text">
                    <strong>
                      {row.label}
                      {row.level === "low" ? <em className="pse-flag pse-flag--warning">{tt("rsLowBadge")}</em> : null}
                      {row.level === "out" ? <em className="pse-flag pse-flag--danger">{tt("rsOutBadge")}</em> : null}
                    </strong>
                    <small>{row.variant.sku || "—"}</small>
                  </span>
                </span>
                <span className="pse-table__num" data-label={isSerialized ? tt("rsColCurrentSerialized") : tt("rsColCurrent")}>
                  {row.current}
                </span>
                <span className="pse-restock-row__center">
                  <Stepper
                    value={quantities[row.variant.id] || ""}
                    onChange={(value) => setQuantity(row.variant.id, value)}
                    max={MAX_RESTOCK_ROW_QUANTITY}
                    ariaLabel={fill(tt("rsQuantityFor"), { variant: row.label })}
                    decreaseLabel={tt("rsDecrease")}
                    increaseLabel={tt("rsIncrease")}
                  />
                </span>
                <span
                  className={cx("pse-table__num", "pse-restock-row__result", quantity > 0 && "is-positive")}
                  data-label={tt("rsColResult")}
                >
                  {row.current + quantity}
                </span>
              </div>
            );
          })}
        </div>
      ) : (
        <EmptyState icon="search" title={tt("rsNoMatch")} />
      )}
      {isSerialized ? <p className="pse-muted pse-small">{tt("rsSerializedCurrentHint")}</p> : null}
    </Drawer>
  );
};

export default RestockDrawer;
